/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react/prop-types */
/*
Author: Granite Bagas
*/

import React, { useMemo } from 'react';
import { View, StyleSheet, TouchableOpacity } from 'react-native';
import { isWeb, fullWidth } from '@global/screens';
import { parseDate } from '@global/helpers';
import Colors from '@global/colors';
import * as Layout from '@components/screen/Layout';
import Table from '@components/table/Table';
import TableMobile from '@components/table/TableMobile';
import SociollaText from '@components/sociolla/SociollaText';
import Image from '@components/global/Image';
import useListScreen from '@/hooks/useListScreen';
import {
  useGetInventorySummaryListQuery,
  useGetInventorySummaryCountQuery,
  useGetInventorySummaryInfiniteListInfiniteQuery,
} from '@api/listApi';

const tableStructure = {
  salesman_name: {
    head: 'Salesman Name',
    body: 'member_name',
  },
  salesman_code: {
    head: 'Salesman Code',
    body: 'member_code',
  },
  sales_team: {
    head: 'Sales Team',
    body: 'sales_team_name',
  },
  updated_at: {
    head: 'Last Updated',
    type: 'slot',
    body: 'last_updated_at',
  },
};

// Search-only list: only the keyword search box (no filter modal), so the transform
// just unwraps the keyword and sets the BE `is_search` flag when present.
const toApiFilter = (rawFilter = {}) => {
  const filter = {};
  const kw = rawFilter.keyword?.$regex ?? rawFilter.keyword;
  if (kw) {
    filter.keyword = kw;
    filter.is_search = true;
  }
  return filter;
};

const InventorySummaryScreen = (props) => {
  // ── list data ──
  const { list, count, openChild, scope, params } = useListScreen({
    listHook: useGetInventorySummaryListQuery,
    countHook: useGetInventorySummaryCountQuery,
    infiniteHook: useGetInventorySummaryInfiniteListInfiniteQuery,
    toFilter: toApiFilter,
  });

  // ── memoized values ──
  // Current search text, derived from the applied keyword filter.
  const searchValue = useMemo(() => {
    const k = params.filter?.keyword;
    return k && k.$regex ? k.$regex : k ?? '';
  }, [params.filter]);

  // Web = flat array; mobile = paged infinite result. id is already mapped by the factory.
  const rows = useMemo(() => {
    return isWeb
      ? Array.isArray(list.data)
        ? list.data
        : []
      : (list.data?.pages ?? []).flat();
  }, [list.data]);

  // ── handlers ──
  const goToDetail = (data) => {
    openChild('InventorySummaryDetailScreen', {
      titleScreen: 'Detail Inventory Summary',
      id: data.member_id,
      sales_team_id: data.sales_team_id,
      go_back_key: props.route.key,
    });
  };

  // ── render ──
  return (
    <Layout.Container>
      {isWeb ? <Layout.Title title="Inventory Summary"></Layout.Title> : false}
      {isWeb ? (
        <Table
          filterName={scope}
          name="inventory"
          structures={tableStructure}
          loading={list.isLoading}
          refetching={list.isFetching}
          data={rows}
          searchKey="keyword"
          showFilter={false}
          searchPlaceholder="Search by Salesman name, Salesman code..."
          searchValue={searchValue}
          searchWidth={460}
          hasNoHide={true}
          hasCallback={true}
          totalData={count.data}
          skip={params.skip}
          limit={params.limit}
          onClickRow={({ data }) => goToDetail(data)}
          slot={{
            last_updated_at: ({ data, index }) => (
              <SociollaText key={`reference-${index}`}>
                {parseDate(data.last_updated_at, 'd MMMM yyyy | HH:mm:ss')}
              </SociollaText>
            ),
          }}
        />
      ) : (
        <TableMobile
          filterName={scope}
          keyExtractor={(item) => String(item.member_id ?? item.id ?? item._id)}
          searchKey="keyword"
          placeholder="Search by Salesman name, Salesman code..."
          searchValue={searchValue}
          loading={list.isLoading}
          refetching={list.isFetching}
          loadingLoadMore={list.isFetchingNextPage}
          hasNextPage={list.hasNextPage}
          onLoadMore={list.fetchNextPage}
          onRefresh={list.refetch}
          data={rows}
          totalData={count.data}
          renderItem={({ data, index }) => (
            <TouchableOpacity
              activeOpacity={0.6}
              style={styles.skpItem}
              onPress={() => goToDetail(data)}
              key={index}>
              <View
                style={[styles.skpItemSection, { alignItems: 'flex-start' }]}>
                <SociollaText
                  type="medium"
                  style={{ fontSize: 15, width: fullWidth - 140 }}>
                  {data.member_name ? data.member_name : '-'}
                </SociollaText>
                <SociollaText
                  type="medium"
                  style={{ fontSize: 14, color: Colors.blue3 }}>
                  {data.sales_team_name ? data.sales_team_name : ''}
                </SociollaText>
              </View>
              <SociollaText
                style={{
                  marginTop: 12,
                  marginBottom: 10,
                  fontSize: 14,
                  color: Colors.grey2,
                }}>
                {data.member_code ? data.member_code : '-'}
              </SociollaText>
              <View style={styles.skpItemSection}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <Image
                    style={{
                      width: 23,
                      height: 16,
                      marginLeft: -5,
                      marginRight: 5,
                    }}
                    width={23}
                    height={16}
                    svg={true}
                    source={require('@assets/img/icons/menu/calendar.svg')}
                  />
                  <SociollaText style={{ fontSize: 12, color: Colors.grey2 }}>
                    {data.last_updated_at
                      ? parseDate(
                          data.last_updated_at,
                          'd MMMM yyyy | HH:mm:ss',
                        )
                      : '-'}
                  </SociollaText>
                </View>
              </View>
            </TouchableOpacity>
          )}
        />
      )}
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  skpItem: {
    width: '100%',
    paddingVertical: 20,
    paddingHorizontal: 10,
    backgroundColor: Colors.white,
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey0,
  },
  skpItemSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: fullWidth - 20,
  },
});

export default InventorySummaryScreen;
